import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import api, { checkMedia, checkPoll, checkSmile, createPoll, createPost, uploadFile } from '@/lib/api';
import { Sparkles, Send, Image as ImageIcon, Video, BarChart2, Star, X, Loader2 } from 'lucide-react';
import { TatemaeModal } from './TatemaeModal';
import { PollReviewModal } from './PollReviewModal';
import { getImageFileFromClipboard, insertTextAtCursor } from '@/utils/pasteImage';
import { autoResizeTextarea } from '@/utils/autoResize';
import { useI18n } from '@/lib/i18n';

export function CreatePost({ user, onPostCreated }: { user: any, onPostCreated: () => void }) {
    const [content, setContent] = useState('');
    const { t } = useI18n();
    const [isChecking, setIsChecking] = useState(false);
    const [suggestion, setSuggestion] = useState<any>(null);
    const [showTatemae, setShowTatemae] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showRewriteConfirm, setShowRewriteConfirm] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [checkPhase, setCheckPhase] = useState<'media' | 'text' | null>(null);

    // Poll State
    const [showPollModal, setShowPollModal] = useState(false);
    const [pollQuestion, setPollQuestion] = useState('');
    const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
    const [pollVisibility, setPollVisibility] = useState<'public' | 'friends' | 'specific'>('public');
    const [pollFriendIds, setPollFriendIds] = useState<number[]>([]);
    const [pollFriends, setPollFriends] = useState<any[]>([]);
    const [pollChecking, setPollChecking] = useState(false);
    const [pollSuggestion, setPollSuggestion] = useState<any>(null);
    const [showPollReview, setShowPollReview] = useState(false);
    const [pollError, setPollError] = useState('');

    // Media State
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [mediaType, setMediaType] = useState<'image' | 'video' | 'text'>('text');
    const [isUploading, setIsUploading] = useState(false);
    const [mediaApproved, setMediaApproved] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const postTextRef = useRef<HTMLTextAreaElement>(null);
    const pollQuestionRef = useRef<HTMLTextAreaElement>(null);
    const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const draftKey = user?.user_id ? `maison_post_draft_${user.user_id}` : null;

    const floor = user?.floor_level || 1;
    const isSpecial = ['Admin', 'Manager', 'Maestro'].includes(user?.role_type);

    const canImage = isSpecial || floor >= 5;
    const canSpotlight = isSpecial || floor >= 6;
    const canVideo = isSpecial || floor >= 7;
    const canPoll = isSpecial || floor >= 9;
    const lockedButtonClassName = 'opacity-30 cursor-not-allowed hover:bg-transparent hover:text-inherit';

    const handleMediaFile = async (file: File) => {
        // Client-side validation
        if (file.type.startsWith('image/') && !canImage) {
            showLockedFeatureToast(t('写真投稿'), 5);
            return;
        }
        if (file.type.startsWith('video/') && !canVideo) {
            showLockedFeatureToast(t('動画投稿'), 7);
            return;
        }

        setIsUploading(true);
        try {
            const res = await uploadFile(file);
            setMediaUrl(res.data.url);
            setMediaType(file.type.startsWith('video/') ? 'video' : 'image');
            setMediaApproved(false);
        } catch (err) {
            console.error("Upload failed", err);
            const detail = (err as any)?.response?.data?.detail;
            alert(detail ? `アップロードに失敗しました: ${detail}` : 'アップロードに失敗しました。');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;
        const file = e.target.files[0];
        await handleMediaFile(file);
        e.currentTarget.value = '';
    };

    const handlePasteImageToPost = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        await handleMediaFile(imageFile);
    };

    const handlePasteImageToText = async (
        e: React.ClipboardEvent<HTMLTextAreaElement>,
        currentValue: string,
        setValue: (value: string) => void
    ) => {
        const imageFile = getImageFileFromClipboard(e);
        if (!imageFile) return;
        e.preventDefault();
        if (!imageFile.type.startsWith('image/')) return;
        try {
            const res = await uploadFile(imageFile);
            const url = res.data?.url;
            if (url) {
                insertTextAtCursor(e.currentTarget, currentValue, `\n${url}\n`, setValue);
            }
        } catch (err) {
            console.error('Paste upload failed', err);
            showToast('画像の貼り付けに失敗しました。');
        }
    };

    const triggerFileSelect = (accept: string) => {
        if (fileInputRef.current) {
            fileInputRef.current.accept = accept;
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearTimeout(toastTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!draftKey || typeof window === 'undefined') return;
        const raw = localStorage.getItem(draftKey);
        if (!raw) return;
        try {
            const draft = JSON.parse(raw);
            if (typeof draft.content === 'string') {
                setContent(draft.content);
            }
            if (typeof draft.mediaUrl === 'string') {
                setMediaUrl(draft.mediaUrl);
            }
            if (draft.mediaType === 'image' || draft.mediaType === 'video' || draft.mediaType === 'text') {
                setMediaType(draft.mediaType);
            }
            setMediaApproved(false);
        } catch (err) {
            console.warn('Failed to parse post draft', err);
        }
    }, [draftKey]);

    useEffect(() => {
        if (!draftKey || typeof window === 'undefined') return;
        if (!content.trim() && !mediaUrl) {
            localStorage.removeItem(draftKey);
            return;
        }
        localStorage.setItem(
            draftKey,
            JSON.stringify({
                content,
                mediaUrl,
                mediaType,
            })
        );
    }, [content, mediaUrl, mediaType, draftKey]);

    useEffect(() => {
        autoResizeTextarea(postTextRef.current);
    }, [content]);

    useEffect(() => {
        autoResizeTextarea(pollQuestionRef.current);
    }, [pollQuestion]);

    const clearDraft = () => {
        if (draftKey && typeof window !== 'undefined') {
            localStorage.removeItem(draftKey);
        }
    };

    useEffect(() => {
        if (!showPollModal) return;
        if (pollVisibility !== 'specific') {
            setPollFriendIds([]);
            return;
        }
        const fetchFriends = async () => {
            try {
                const res = await api.get('/friends/');
                setPollFriends(res.data || []);
            } catch (err) {
                console.error('Failed to load friends', err);
            }
        };
        fetchFriends();
    }, [showPollModal, pollVisibility]);

    const renderPortal = (node: ReactNode) => {
        if (typeof document === 'undefined') return null;
        return createPortal(node, document.body);
    };

    const showToast = (message: string) => {
        setToastMessage(message);
        if (toastTimerRef.current) {
            clearTimeout(toastTimerRef.current);
        }
        toastTimerRef.current = setTimeout(() => {
            setToastMessage('');
        }, 5000);
    };

    const getLockedFeatureMessage = (featureLabel: string, unlockFloor: number) => (
        t('{feature}機能はまだ未開放です。Floor{floor}で解放されます。', {
            feature: featureLabel,
            floor: unlockFloor,
        })
    );

    const showLockedFeatureToast = (featureLabel: string, unlockFloor: number) => {
        showToast(getLockedFeatureMessage(featureLabel, unlockFloor));
    };

    const resetPollForm = () => {
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollVisibility('public');
        setPollFriendIds([]);
        setPollError('');
        setPollSuggestion(null);
        setShowPollReview(false);
    };

    const handleAddOption = () => {
        if (pollOptions.length >= 20) return;
        setPollOptions([...pollOptions, '']);
    };

    const handleRemoveOption = (index: number) => {
        if (pollOptions.length <= 2) return;
        setPollOptions(pollOptions.filter((_, idx) => idx !== index));
    };

    const handleOptionChange = (index: number, value: string) => {
        setPollOptions(pollOptions.map((opt, idx) => (idx === index ? value : opt)));
    };

    const togglePollFriend = (friendId: number) => {
        setPollFriendIds((prev) => (
            prev.includes(friendId) ? prev.filter((id) => id !== friendId) : [...prev, friendId]
        ));
    };

    const handlePollSubmit = async () => {
        const question = pollQuestion.trim();
        const options = pollOptions.map((opt) => opt.trim()).filter(Boolean);
        setPollError('');

        if (!question) {
            setPollError('質問を入力してください。');
            return;
        }
        if (options.length < 2) {
            setPollError('選択肢は2つ以上入力してください。');
            return;
        }
        if (options.length > 20) {
            setPollError('選択肢は最大20個までです。');
            return;
        }
        if (pollVisibility === 'specific' && pollFriendIds.length === 0) {
            setPollError('指定フレンドのみの場合は、対象の隣人を選択してください。');
            return;
        }

        setPollChecking(true);
        try {
            const checkRes = await checkPoll(question, options);
            const data = checkRes.data;
            if (data?.error) {
                setPollError(`AI校正に失敗しました: ${data.error}`);
                return;
            }
            if (data.is_rewritten) {
                setPollSuggestion(data);
                setShowPollReview(true);
                return;
            }
            await createPoll({
                question,
                options,
                visibility: pollVisibility,
                allowed_friend_ids: pollVisibility === 'specific' ? pollFriendIds : [],
                was_rewritten: false,
                accepted_ai: false,
                is_positive: true,
            });
            resetPollForm();
            setShowPollModal(false);
            showToast('アンケートを作成しました。');
            onPostCreated();
        } catch (err: any) {
            setPollError(err?.response?.data?.detail || 'アンケートの作成に失敗しました。');
        } finally {
            setPollChecking(false);
        }
    };

    const handlePost = async () => {
        if (!content.trim() && !mediaUrl) return;
        setShowConfirm(true);
    };

    const handleConfirmPost = async () => {
        setShowConfirm(false);

        const trimmedContent = content.trim();
        setIsChecking(true);
        try {
            if (mediaUrl && mediaType !== 'text' && !mediaApproved) {
                setCheckPhase('media');
                const mediaRes = await checkMedia(mediaUrl, mediaType, trimmedContent);
                const mediaData = mediaRes.data;
                if (mediaData?.approved === false) {
                    const reasonText = mediaData?.reason ? `\n\n理由:\n${mediaData.reason}` : '';
                    const guidanceText = mediaData?.guidance ? `\n\n目安:\n${mediaData.guidance}` : '';
                    alert(
                        '検閲の結果、この動画・画像は投稿できないと判断されました。\n' +
                        'もし問題ない内容だと思われる場合は、お手数ですがお問い合わせフォームからご連絡ください。\n' +
                        '可能であれば、該当の動画や画像を添付していただけると確認がスムーズです。' +
                        reasonText +
                        guidanceText
                    );
                    return;
                }
                setMediaApproved(true);
            }

            if (!trimmedContent) {
                await submitPost('', {
                    was_rewritten: false,
                    accepted_ai: false,
                    is_positive: true
                }, '投稿が完了しました。');
                return;
            }

            setCheckPhase('text');
            const checkRes = await checkSmile(trimmedContent);
            const data = checkRes.data;

            if (data?.error) {
                alert(`AI校正に失敗しました: ${data.error}`);
                return;
            }

            if (data.is_rewritten) {
                setSuggestion(data);
                setShowTatemae(true);
                return;
            }

            await submitPost(content, {
                was_rewritten: false,
                accepted_ai: false,
                is_positive: data?.is_positive !== false
            }, '素晴らしい投稿です！そのまま投稿します。');
        } catch (err: any) {
            console.error(err);
            alert(`AI校正に失敗しました: ${err?.message || 'AIチェックに失敗しました'}`);
        } finally {
            setIsChecking(false);
            setCheckPhase(null);
        }
    };

    const submitPost = async (finalContent: string, aiData: any, toastText?: string) => {
        try {
            await createPost({
                content: finalContent.trim(),
                was_rewritten: !!aiData.was_rewritten,
                accepted_ai: !!aiData.accepted_ai,
                is_positive: !!aiData.is_positive,
                image_path: mediaUrl,
                media_check_passed: mediaApproved,
                post_type: mediaType === 'video' ? 'video' : 'text' // Backend treats image as 'text' type with image_path usually, or I should verify. 
                // Wait, backend logic: if post_in.post_type == 'video' ...
                // If I send 'text' with image_path, backend validates 'image' restriction: 
                // "if post_in.image_path and floor < 5..." -> This is fine.
                // But for video it checks "if post_in.post_type == 'video'".
                // So if it's image, I can send 'text' (default) or 'image'. 
                // Let's send 'text' for image (standard timeline post with image attachment) and 'video' for video.
            });
            setContent('');
            setMediaUrl(null);
            setMediaType('text');
            setMediaApproved(false);
            setSuggestion(null);
            setShowTatemae(false);
            clearDraft();
            if (toastText) {
                showToast(toastText);
            }
            onPostCreated();
        } catch (err) {
            console.error("Failed to post details", err);
            const detail = (err as any)?.response?.data?.detail;
            alert(detail ? `投稿に失敗しました: ${detail}` : '投稿に失敗しました。時間をおいて再度お試しください。');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6 border border-gray-100">
            <textarea
                ref={postTextRef}
                className="w-full p-4 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-base"
                rows={3}
                placeholder={t('あなたの幸せをシェア...')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                onPaste={handlePasteImageToPost}
                onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault();
                        handlePost();
                    }
                }}
            />
            <div className="text-xs text-gray-400 mt-1">{t('Enterで改行 / Ctrl+Enterで送信')}</div>

            {/* Media Preview */}
            {mediaUrl && (
                <div className="relative mt-4 rounded-lg overflow-hidden bg-gray-100 border max-h-[300px] flex justify-center">
                    {mediaType === 'video' ? (
                        <video src={mediaUrl} controls className="max-h-full max-w-full" />
                    ) : (
                        <img src={mediaUrl} alt="Preview" className="max-h-full max-w-full object-contain" />
                    )}
                    <button
                        onClick={() => { setMediaUrl(null); setMediaType('text'); setMediaApproved(false); }}
                        className="absolute top-2 right-2 bg-black/50 hover:bg-black text-white p-1 rounded-full text-xs"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Hidden Input */}
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileSelect}
            />

            <TatemaeModal
                isOpen={showTatemae && !!suggestion}
                data={suggestion}
                onClose={() => {
                    setShowTatemae(false);
                    setSuggestion(null);
                }}
                onAccept={() => submitPost(suggestion.converted, {
                    was_rewritten: true,
                    accepted_ai: true,
                    is_positive: false
                }, '投稿が完了しました。')}
                onSelfEdit={() => {
                    setShowTatemae(false);
                    setShowRewriteConfirm(true);
                }}
            />

            <PollReviewModal
                isOpen={showPollReview && !!pollSuggestion}
                data={pollSuggestion}
                onClose={() => {
                    setShowPollReview(false);
                    setPollSuggestion(null);
                }}
                onAccept={async () => {
                    if (!pollSuggestion) return;
                    const convertedQuestion = pollSuggestion.question?.converted || '';
                    const convertedOptions = (pollSuggestion.options || []).map((opt: any) => opt.converted || '').filter(Boolean);
                    try {
                        await createPoll({
                            question: convertedQuestion,
                            options: convertedOptions,
                            visibility: pollVisibility,
                            allowed_friend_ids: pollVisibility === 'specific' ? pollFriendIds : [],
                            was_rewritten: true,
                            accepted_ai: true,
                            is_positive: false,
                        });
                        resetPollForm();
                        setShowPollReview(false);
                        setShowPollModal(false);
                        showToast('アンケートを作成しました。');
                        onPostCreated();
                    } catch (err: any) {
                        setPollError(err?.response?.data?.detail || 'アンケートの作成に失敗しました。');
                    }
                }}
                onSelfEdit={() => {
                    if (pollSuggestion) {
                        setPollQuestion(pollSuggestion.question?.converted || '');
                        const converted = (pollSuggestion.options || []).map((opt: any) => opt.converted || '').filter(Boolean);
                        setPollOptions(converted.length >= 2 ? converted : [converted[0] || '', '']);
                    }
                    setShowPollReview(false);
                    setPollSuggestion(null);
                }}
            />

            {showPollModal && renderPortal(
                <div className="fixed inset-0 z-[4500] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full border border-[#800020]/20 p-6 space-y-4 max-h-[85vh] overflow-y-auto">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-lg font-bold text-[#800020]">アンケートを作成</h3>
                                <p className="text-xs text-gray-600 mt-1">1日3件まで作成できます。</p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowPollModal(false);
                                    setPollError('');
                                }}
                                className="text-gray-400 hover:text-gray-600 text-xl"
                            >
                                &times;
                            </button>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">公開範囲</label>
                            <select
                                value={pollVisibility}
                                onChange={(e) => setPollVisibility(e.target.value as typeof pollVisibility)}
                                className="w-full border rounded-md p-2 text-sm"
                            >
                                <option value="public">全体</option>
                                <option value="friends">フレンドのみ</option>
                                <option value="specific">指定フレンドのみ</option>
                            </select>
                            {pollVisibility === 'specific' && (
                                <div className="mt-2 border rounded-md p-3 max-h-40 overflow-y-auto">
                                    {pollFriends.length === 0 ? (
                                        <div className="text-xs text-gray-500">選択できる隣人がいません。</div>
                                    ) : (
                                        <div className="space-y-2">
                                            {pollFriends.map((friend) => (
                                                <label key={friend.user_id} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={pollFriendIds.includes(friend.user_id)}
                                                        onChange={() => togglePollFriend(friend.user_id)}
                                                    />
                                                    <span>{friend.display_name?.trim() || friend.room_no || '住人'}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">質問</label>
                            <textarea
                                ref={pollQuestionRef}
                                value={pollQuestion}
                                onChange={(e) => setPollQuestion(e.target.value)}
                                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                                onPaste={(e) => handlePasteImageToText(e, pollQuestion, setPollQuestion)}
                                rows={3}
                                className="w-full border rounded-md p-2 text-sm resize-none"
                                placeholder="例: どちらの映画が好き？"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-semibold text-gray-700">選択肢（最大20個）</label>
                                <button
                                    className="text-xs text-[#800020] hover:underline"
                                    onClick={handleAddOption}
                                    disabled={pollOptions.length >= 20}
                                >
                                    追加する
                                </button>
                            </div>
                            <div className="space-y-2">
                                {pollOptions.map((option, index) => (
                                    <div key={`option-${index}`} className="flex items-center gap-2">
                                        <input
                                            value={option}
                                            onChange={(e) => handleOptionChange(index, e.target.value)}
                                            className="flex-1 border rounded-md p-2 text-sm"
                                            placeholder={`選択肢 ${index + 1}`}
                                        />
                                        <button
                                            className="text-xs text-gray-400 hover:text-gray-600"
                                            onClick={() => handleRemoveOption(index)}
                                            disabled={pollOptions.length <= 2}
                                        >
                                            削除
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {pollError && (
                            <div className="text-xs text-red-500">{pollError}</div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowPollModal(false);
                                    setPollError('');
                                }}
                            >
                                閉じる
                            </Button>
                            <Button
                                onClick={handlePollSubmit}
                                disabled={pollChecking}
                                className="bg-[#800020] hover:bg-[#600018] text-white"
                            >
                                {pollChecking ? '検閲中...' : '作成する'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showConfirm && renderPortal(
                <div className="fixed inset-0 z-[4000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full border-2 border-[#800020] p-6 space-y-4">
                        <h3 className="text-lg font-bold text-[#800020]">執事からのメッセージ</h3>
                        <p className="text-sm text-gray-600">投稿はAI検閲の対象です。念のため確認させてくださいね。</p>
                        <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                            <li>校正なし承認: +5</li>
                            <li>校正が入った時点で -3</li>
                            <li>AI校正文を採用すると +5（合計 +2）</li>
                            <li>不適切表現は強制的に書き換えられます</li>
                            <li>ほんとにポジティブだって言い切れます？</li>
                        </ul>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button onClick={handleConfirmPost} className="bg-[#800020] hover:bg-[#600018] text-white px-6">
                                実行する
                            </Button>
                            <Button variant="outline" onClick={() => setShowConfirm(false)} className="px-6">
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showRewriteConfirm && renderPortal(
                <div className="fixed inset-0 z-[4000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full border-2 border-[#800020] p-6 space-y-4">
                        <h3 className="text-lg font-bold text-[#800020]">執事からのメッセージ</h3>
                        <p className="text-sm text-gray-600">
                            書き直しを選んだ場合、今アドバイスした内容をアレンジしても、また検閲に引っかかる可能性がありますよ。<br />
                            そのままアドバイス通りに書いても引っかかるかも。検閲官は気が変わりやすいですからね。
                        </p>
                        <div className="flex justify-center gap-3 pt-2">
                            <Button
                                onClick={() => {
                                    setShowRewriteConfirm(false);
                                    if (suggestion?.converted) {
                                        setContent(suggestion.converted);
                                    }
                                    setSuggestion(null);
                                }}
                                className="bg-[#800020] hover:bg-[#600018] text-white px-6"
                            >
                                それでも書き直す
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowRewriteConfirm(false);
                                    if (suggestion) {
                                        setShowTatemae(true);
                                    }
                                }}
                                className="px-6"
                            >
                                戻る
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {toastMessage && renderPortal(
                <div className="fixed left-1/2 top-4 z-[4500] w-[min(calc(100vw-1.5rem),28rem)] -translate-x-1/2 rounded-xl bg-black/85 px-4 py-3 text-sm leading-relaxed text-white shadow-lg break-words md:left-auto md:right-5 md:top-20 md:w-auto md:max-w-md md:translate-x-0">
                    {toastMessage}
                </div>
            )}

            <div className="mt-4 flex justify-between items-center">
                <div className="flex gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        disabled={isUploading}
                        aria-disabled={!canImage}
                        title={canImage ? t('写真投稿') : getLockedFeatureMessage(t('写真投稿'), 5)}
                        className={!canImage ? lockedButtonClassName : ""}
                        onClick={() => {
                            if (!canImage) {
                                showLockedFeatureToast(t('写真投稿'), 5);
                                return;
                            }
                            triggerFileSelect("image/*");
                        }}
                    >
                        <ImageIcon size={20} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-disabled={!canSpotlight}
                        title={canSpotlight ? t('スポットライト') : getLockedFeatureMessage(t('スポットライト'), 6)}
                        className={!canSpotlight ? lockedButtonClassName : ""}
                        onClick={() => {
                            if (!canSpotlight) {
                                showLockedFeatureToast(t('スポットライト'), 6);
                            }
                        }}
                    >
                        <Star size={20} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        disabled={isUploading}
                        aria-disabled={!canVideo}
                        title={canVideo ? t('動画投稿') : getLockedFeatureMessage(t('動画投稿'), 7)}
                        className={!canVideo ? lockedButtonClassName : ""}
                        onClick={() => {
                            if (!canVideo) {
                                showLockedFeatureToast(t('動画投稿'), 7);
                                return;
                            }
                            triggerFileSelect("video/*");
                        }}
                    >
                        <Video size={20} />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-disabled={!canPoll}
                        title={canPoll ? t('アンケート') : getLockedFeatureMessage(t('アンケート'), 9)}
                        className={!canPoll ? lockedButtonClassName : ""}
                        onClick={() => {
                            if (!canPoll) {
                                showLockedFeatureToast(t('アンケート'), 9);
                                return;
                            }
                            setShowPollModal(true);
                        }}
                    >
                        <BarChart2 size={20} />
                    </Button>
                </div>

                <div className="flex items-center gap-2">
                    {isUploading && (
                        <span className="flex items-center gap-2 text-xs font-bold text-white bg-[#800020] px-3 py-1 rounded-full shadow animate-pulse">
                            <Loader2 size={12} className="animate-spin" />
                            Uploading...
                        </span>
                    )}
                    <Button onClick={handlePost} disabled={isChecking || isUploading || (!content.trim() && !mediaUrl)} className="gap-2">
                        {isChecking ? <Sparkles className="animate-spin" size={16} /> : <Send size={16} />}
                        {isChecking
                            ? (checkPhase === 'media' ? t('執事による検閲中...') : t('執事が推敲中...'))
                            : t('投稿する')}
                    </Button>
                </div>
            </div>
            <div className="mt-2 text-[11px] leading-relaxed text-gray-500">
                AI判定 基準: 画像は JPEG / PNG / WebP / GIF、動画は音声あり約45分・音声なし約60分まで
            </div>
        </div>
    );
}
